import {
  LayoutDashboard,
  Smartphone,
  Package,
  Settings,
  Building2,
  CreditCard,
  ChevronDown,
  Check,
  MapPin,
  Plus,
} from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Link } from '@tanstack/react-router';
import { NavMenuLink } from '@/features/navigation/components/nav-menu/subcomponents/nav-menu-link/NavMenuLink';
import { ProsumoLogo } from '@/common/components/prosumo-logo/ProsumoLogo';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/common/components/ui/dropdown-menu';
import { useSupplyPoint } from '@/features/supply-points/context/useSupplyPoint.ts';

export function NavMenu() {
  const { supplyPoints, activeSupplyPoint, setActiveSupplyPointId } = useSupplyPoint();
  const hasSupplyPoints = supplyPoints.length > 0;

  return (
    <div className="flex h-full flex-col">
      {/* Logo */}
      <div className="flex shrink-0 flex-col items-center justify-center pb-8 pt-10">
        <ProsumoLogo />
      </div>

      {/* Main nav group */}
      <div className="flex-1 overflow-y-auto px-4 pb-4">
        <div className="flex flex-col gap-2 rounded-2xl bg-muted p-2">
          {/* Supply point switcher */}
          {hasSupplyPoints ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  aria-label={t`Select supply point`}
                  className="flex w-full items-center justify-between rounded-xl bg-card px-4 py-3 text-sm transition-colors hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <div className="flex flex-col items-start truncate">
                    <span className="mb-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                      <Trans>Supply point</Trans>
                    </span>
                    <span className="truncate font-semibold text-foreground">
                      {activeSupplyPoint?.name}
                    </span>
                  </div>
                  <ChevronDown size={16} className="shrink-0 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="min-w-[240px]">
                {supplyPoints.map((sp) => (
                  <DropdownMenuItem
                    key={sp.id}
                    onClick={() => setActiveSupplyPointId(sp.id)}
                    className="flex items-center justify-between"
                  >
                    <span className="truncate">{sp.name}</span>
                    {activeSupplyPoint?.id === sp.id && (
                      <Check size={16} className="ml-2 shrink-0 text-primary" />
                    )}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/supply-points/new" className="flex items-center gap-2">
                    <Plus size={14} className="shrink-0" />
                    <Trans>Add supply point</Trans>
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="flex w-full items-center gap-3 rounded-xl bg-card px-4 py-3 text-sm">
              <MapPin size={16} className="shrink-0 text-muted-foreground" />
              <div className="flex flex-col items-start truncate">
                <span className="mb-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  <Trans>Supply point</Trans>
                </span>
                <span className="text-xs text-muted-foreground">
                  <Trans>No supply points</Trans>
                </span>
              </div>
            </div>
          )}

          {/* Supply point–specific nav items */}
          {hasSupplyPoints ? (
            <nav aria-label={t`Supply point navigation`} className="flex flex-col gap-1 px-1 pb-1">
              <ul className="flex flex-col gap-1">
                <li>
                  <NavMenuLink
                    to="/"
                    exact
                    label={<Trans>Overview</Trans>}
                    icon={<LayoutDashboard className="h-[18px] w-[18px] shrink-0" />}
                  />
                </li>
                <li>
                  <NavMenuLink
                    to="/devices"
                    label={<Trans>Devices</Trans>}
                    icon={<Smartphone className="h-[18px] w-[18px] shrink-0" />}
                  />
                </li>
                <li>
                  <NavMenuLink
                    to="/products"
                    label={<Trans>Products</Trans>}
                    icon={<Package className="h-[18px] w-[18px] shrink-0" />}
                  />
                </li>
                <li>
                  <NavMenuLink
                    to="/settings"
                    label={<Trans>Settings</Trans>}
                    icon={<Settings className="h-[18px] w-[18px] shrink-0" />}
                  />
                </li>
              </ul>
            </nav>
          ) : (
            <div className="px-2 pb-1 pt-0.5 text-center">
              <p className="mb-2 text-xs text-muted-foreground">
                <Trans>Create a supply point to access monitoring features.</Trans>
              </p>
              <Link
                to="/supply-points/new"
                className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
              >
                <Plus size={12} />
                <Trans>Create supply point</Trans>
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Bottom nav — global, not supply point–specific */}
      <div className="shrink-0 px-4 pb-4">
        <nav aria-label={t`Global navigation`}>
          <ul className="flex flex-col gap-1">
            <li>
              <NavMenuLink
                to="/organizations"
                label={<Trans>Organizations</Trans>}
                icon={<Building2 className="h-[18px] w-[18px] shrink-0" />}
                variant="bottom"
              />
            </li>
            <li>
              <NavMenuLink
                to="/billing"
                label={<Trans>Billing</Trans>}
                icon={<CreditCard className="h-[18px] w-[18px] shrink-0" />}
                variant="bottom"
              />
            </li>
          </ul>
        </nav>
      </div>
    </div>
  );
}
